<template>
  <v-card class="payment-dialog">
    <!-- Header mejorado -->
    <v-card-title class="payment-header white--text">
      <v-icon left color="white" size="28">
        {{ type === 1 ? "mdi-cash-minus" : "mdi-cash-plus" }}
      </v-icon>
      <span class="text-h5 font-weight-medium">
        {{ type === 1 ? "Registro de Pago" : "Registro de Cobro" }}
      </span>
      <v-spacer></v-spacer>
      <v-btn icon color="white" @click="closeDialog">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text class="pa-0">
      <v-container fluid class="pa-6">
        <!-- Sección de configuración básica -->
        <v-card outlined class="mb-6 section-card">
          <v-card-subtitle class="section-header">
            <v-icon left color="primary">mdi-cog</v-icon>
            <span class="font-weight-medium">Configuración Básica</span>
          </v-card-subtitle>

          <v-card-text class="pt-2">
            <v-row>
              <v-col cols="12" md="3">
                <v-autocomplete
                  @change="updateBanks"
                  v-model="paymentData.business_unit_id"
                  :items="businessUnits"
                  label="Unidad de negocio"
                  color="primary"
                  outlined
                  dense
                  required
                  prepend-inner-icon="mdi-office-building"
                  :rules="[(v) => !!v || 'Campo requerido']"
                />
              </v-col>

              <v-col cols="12" md="3">
                <v-autocomplete
                  v-model="paymentData.location_id"
                  :items="
                    locations.filter(
                      (element) => element.bus_id == paymentData.business_unit_id
                    )
                  "
                  color="primary"
                  label="Ubicación"
                  outlined
                  dense
                  required
                  clearable
                  prepend-inner-icon="mdi-map-marker"
                  :rules="[(v) => !!v || 'Campo requerido']"
                />
              </v-col>

              <v-col cols="12" md="3">
                <v-autocomplete
                  v-model="paymentData.department"
                  :items="departments"
                  return-object
                  color="primary"
                  label="Departamento"
                  outlined
                  dense
                  required
                  clearable
                  prepend-inner-icon="mdi-domain"
                />
              </v-col>

              <v-col cols="12" md="3">
                <v-autocomplete
                  return-object
                  outlined
                  dense
                  v-model="paymentData.bank_account_id"
                  :items="accountBank"
                  label="Cuenta de banco"
                  color="primary"
                  prepend-inner-icon="mdi-bank"
                  item-text="text"
                  item-id="account_id"
                  required @change="set_currency()"
                  :rules="[(v) => !!v || 'Campo requerido']"
                >
                  <template #item="{ item }">
                    <div class="d-flex align-center py-2">
                      <v-avatar size="32" class="mr-3">
                        <v-img
                          contain
                          :src="getIconBank(item.account_bank_id)"
                          :alt="item.bank_description_long"
                        >
                          <template v-slot:placeholder>
                            <v-icon color="grey lighten-1">mdi-bank</v-icon>
                          </template>
                        </v-img>
                      </v-avatar>
                      <div>
                        <div class="font-weight-medium">{{ item.text }}</div>
                        <div class="caption grey--text">
                          {{ item.currency_description }}
                        </div>
                      </div>
                    </div>
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-row>
          <!-- Información de entidad -->
          <v-col cols="12" lg="6">
            <v-card outlined class="h-100 section-card">
              <v-card-subtitle class="section-header">
                <v-icon left color="primary">{{ sectionIcon }}</v-icon>
                <span class="font-weight-medium">{{ sectionTitle }}</span>
                <v-spacer></v-spacer>
                <v-chip
                  v-if="!useEntity"
                  x-small
                  color="primary"
                  outlined
                  class="font-weight-medium"
                >
                  <v-icon left x-small>mdi-book-open-variant</v-icon>
                  Modo contable
                </v-chip>
              </v-card-subtitle>

              <v-card-text class="pt-2">

                <v-row class="pt-2" no-gutters>
                  <v-col cols="12">
                    <v-checkbox
                      v-model="useEntity"
                      :label="type === 2 ? 'Usar cliente' : 'Usar proveedor'"
                      color="primary"
                      hide-details
                      dense
                      class="mt-0 mb-2"
                      @change="onUseEntityChange"
                    />
                  </v-col>
                </v-row>

                <!-- Información de la entidad seleccionada -->
                <v-row v-if="useEntity" class="pt-2">
                  <v-col md="9">
                    <v-text-field
                      dense
                      outlined
                      readonly
                      :label="type === 2 ? 'Cliente' : 'Proveedor'"
                      v-model="entity.name"
                      prepend-inner-icon="mdi-account"
                      :placeholder="`Seleccionar ${type === 2 ? 'cliente' : 'proveedor'}`"
                    />
                  </v-col>
                  <v-col md="2">
                    <searchClients
                    :types_clients="type"
                    @close="dialogSearch = false"
                    @selected-item="selectItem($event)"
                  />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      dense
                      outlined
                      readonly
                      label="RNC o Cédula"
                      v-model="entity.document_id"
                      prepend-inner-icon="mdi-card-account-details"
                    />
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field
                      dense
                      outlined
                      readonly
                      label="Teléfono"
                      v-model="entity.phone_number"
                      prepend-inner-icon="mdi-phone"
                    />
                  </v-col>

                  <v-col cols="12">
                    <v-textarea
                      dense
                      outlined
                      label="Nota"
                      v-model="paymentData.note"
                      prepend-inner-icon="mdi-note-text"
                      rows="2"
                      auto-grow
                      counter="255"
                    />
                  </v-col>
                </v-row>

                <!-- Contrapartida: cuenta contable -->
                <v-row v-else class="pt-2">
                  <v-col cols="12">
                    <v-autocomplete
                      dense
                      outlined
                      v-model="counterAccount"
                      :items="accounts"
                      item-text="name"
                      return-object
                      label="Cuenta contable (contrapartida)"
                      color="primary"
                      prepend-inner-icon="mdi-book-open-page-variant"
                      :rules="[(v) => !!v || 'Campo requerido']"
                    />
                  </v-col>

                  <v-col cols="12">
                    <v-textarea
                      dense
                      outlined
                      label="Nota"
                      v-model="paymentData.note"
                      prepend-inner-icon="mdi-note-text"
                      rows="2"
                      auto-grow
                      counter="255"
                    />
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Detalles del pago -->
          <v-col cols="12" lg="6">
            <v-card outlined class="h-100 section-card">
              <v-card-subtitle class="section-header">
                <v-icon left color="primary">mdi-cash-multiple</v-icon>
                <span class="font-weight-medium">Detalles del Pago</span>
              </v-card-subtitle>

              <v-card-text class="pt-2">
                <v-row>
                  <v-col cols="12" md="6">
                    <v-menu
                      v-model="memu_effective_date"
                      :close-on-content-click="false"
                      :nudge-right="10"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          dense
                          outlined
                          label="Fecha de pago"
                          v-model="paymentDateFormat"
                          prepend-inner-icon="mdi-calendar"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          :rules="[(v) => !!v || 'Campo requerido']"
                        />
                      </template>
                      <v-date-picker v-model="paymentData.payment_date" color="primary" @input="memu_effective_date = false" />
                    </v-menu>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-menu
                      v-model="memu_accounting_date"
                      :close-on-content-click="false"
                      :nudge-right="10"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          dense
                          outlined
                          label="Fecha contable"
                          v-model="paymentData.payment_date_account"
                          prepend-inner-icon="mdi-calendar-check"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                        />
                      </template>
                      <v-date-picker
                        v-model="paymentData.payment_date_account"
                        :min="datesAccounting.start_date"
                        :max="datesAccounting.end_date"
                        @input="memu_accounting_date = false"
                        color="primary"
                      />
                    </v-menu>
                  </v-col>

                  <!-- <v-col cols="12" md="6">
                    <v-autocomplete
                      dense
                      v-model="paymentData.entity_payment_type"
                      :items="entityPaymentTypes"
                      color="primary"
                      prepend-inner-icon="mdi-format-list-checks"
                      item-text="name"
                      item-value="id"
                      return-object
                      label="Tipo de pago"
                      outlined
                      required
                      :rules="[(v) => !!v || 'Campo requerido']"
                    />
                  </v-col> -->

                  <v-col cols="12" md="6">
                    <v-autocomplete
                      dense
                      v-model="paymentData.invoice_payment_type"
                      :items="paymentsType.filter((element)=>element.id != 12)"
                      outlined
                      item-text="name"
                      item-value="id"
                      color="primary"
                      label="Método de pago"
                      prepend-inner-icon="mdi-credit-card"
                      required
                      return-object
                      :rules="[(v) => !!v || 'Campo requerido']"
                    />
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-autocomplete
                      dense
                      v-model="paymentData.currency"
                      :items="currencies"
                      color="primary"
                      prepend-inner-icon="mdi-currency-usd"
                      item-text="description"
                      item-value="id"
                      return-object
                      label="Moneda"
                      outlined
                      required
                      @change="onCurrencyChange"
                      :rules="[(v) => !!v || 'Campo requerido']"
                    />
                  </v-col>

                  <v-col v-if="showExchangeRate" cols="12" md="6">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          dense
                          outlined
                          label="Tasa de cambio"
                          v-model.number="paymentData.currency_rate"
                          type="number"
                          min="0"
                          step="0.0001"
                          prepend-inner-icon="mdi-swap-horizontal"
                          background-color="blue-grey lighten-5"
                          v-bind="attrs"
                          v-on="on"
                          :rules="[(v) => v > 0 || 'Tasa inválida']"
                        />
                      </template>
                      <span>
                        1 {{ baseCurrencyCode }}
                        = {{ paymentData.currency_rate }} {{ paymentData.currency ? paymentData.currency.description : '' }}
                      </span>
                    </v-tooltip>
                  </v-col>

                  <v-col cols="12" :md="showExchangeRate ? 12 : 6">
                    <v-text-field
                      dense
                      outlined
                      label="Monto"
                      v-model.number="paymentData.amount"
                      type="number"
                      min="0"
                      step="0.01"
                      prepend-inner-icon="mdi-cash"
                      :rules="[
                        (v) => !!v || 'Campo requerido',
                        (v) => v > 0 || 'El monto debe ser mayor a 0',
                      ]"
                      :suffix="
                        paymentData.currency ? paymentData.currency.description : ''
                      "
                    />
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Resumen del asiento contable -->
        <v-expand-transition>
          <v-card v-if="canShowSummary" outlined class="mt-6 section-card">
            <v-card-subtitle class="section-header">
              <v-icon left color="primary">mdi-scale-balance</v-icon>
              <span class="font-weight-medium">Resumen del Asiento</span>
              <v-spacer></v-spacer>
              <v-chip x-small color="grey lighten-3" class="font-weight-medium">
                {{ type === 1 ? 'Pago' : 'Cobro' }}
              </v-chip>
            </v-card-subtitle>
            <v-card-text class="pt-4 pb-2">
              <v-row align="center" no-gutters>
                <v-col cols="12" md="5">
                  <div class="text-center pa-2">
                    <v-chip
                      small
                      color="red lighten-1"
                      text-color="white"
                      class="font-weight-bold mb-2"
                    >
                      <v-icon left x-small>mdi-arrow-up</v-icon>
                      DÉBITO
                    </v-chip>
                    <div class="text-body-2 font-weight-medium text-truncate">
                      {{ summary.debit.label }}
                    </div>
                    <div class="text-h6 red--text font-weight-bold mt-1">
                      {{ currencyFormat(summary.baseAmount, baseCurrencyIso) }}
                      <span class="caption grey--text">{{ baseCurrencyCode }}</span>
                    </div>
                    <div v-if="summary.showForeign" class="caption grey--text">
                      {{ currencyFormat(paymentData.amount, paymentData.currency.code) }}
                      {{ paymentData.currency.description }}
                      @ {{ paymentData.currency_rate }}
                    </div>
                  </div>
                </v-col>

                <v-col cols="12" md="2" class="text-center">
                  <v-icon size="32" color="primary">mdi-arrow-right-bold</v-icon>
                </v-col>

                <v-col cols="12" md="5">
                  <div class="text-center pa-2">
                    <v-chip
                      small
                      color="green lighten-1"
                      text-color="white"
                      class="font-weight-bold mb-2"
                    >
                      <v-icon left x-small>mdi-arrow-down</v-icon>
                      CRÉDITO
                    </v-chip>
                    <div class="text-body-2 font-weight-medium text-truncate">
                      {{ summary.credit.label }}
                    </div>
                    <div class="text-h6 green--text font-weight-bold mt-1">
                      {{ currencyFormat(summary.baseAmount, baseCurrencyIso) }}
                      <span class="caption grey--text">{{ baseCurrencyCode }}</span>
                    </div>
                    <div v-if="summary.showForeign" class="caption grey--text">
                      {{ currencyFormat(paymentData.amount, paymentData.currency.code) }}
                      {{ paymentData.currency.description }}
                      @ {{ paymentData.currency_rate }}
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-expand-transition>

      </v-container>
    </v-card-text>

    <!-- Footer con acciones -->
    <v-divider></v-divider>
    <v-card-actions class="pa-4">
      <v-spacer></v-spacer>
      <v-btn color="grey darken-1" text @click="closeDialog" class="mx-2">
        <v-icon left>mdi-cancel</v-icon>
        Cancelar
      </v-btn>
      <v-btn
        :loading="loading"
        :disabled="!isFormValid"
        color="success"
        @click="save()"
        class="mx-2"
      >
        <v-icon left>mdi-content-save</v-icon>
        Guardar {{ type === 1 ? "Pago" : "Cobro" }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import moment from "moment";
import searchClients from "@/components/Income/SearchClientOrSupplier.vue";

export default {
  name: "PaymentDialog",
  components: { searchClients },
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    type: {
      type: Number,
      default: 1, // 1: pagos, 2: cobros
    },
    businessUnits: {
      type: Array,
      default: () => [],
    },
    locations: {
      type: Array,
      default: () => [],
    },
    departments: {
      type: Array,
      default: () => [],
    },
    paymentsType: {
      type: Array,
      default: () => [],
    },
    currencies: {
      type: Array,
      default: () => [],
    },
    entityPaymentTypes: {
      type: Array,
      default: () => [],
    },
    accounts: {
      type: Array,
      default: () => [],
    },
    categories: {
      type: Array,
      default: () => [],
    },
    categoryDgii: {
      type: Array,
      default: () => [],
    },
    filters: {
      type: Object,
      default: () => {},
    },

  },
  data() {
    return {
      dialogInternal: false,
      loading: false,
      dialogSearch: false,
      accountBank: [],
      memu_effective_date: false,
      memu_accounting_date: false,
      useEntity: true,
      counterAccount: null,
      entity: {
        entity_id: "",
        document_id: "",
        name: "",
        email: "",
        phone_number: "",
      },
      paymentData: {
        business_unit_id: "",
        location_id: "",
        department: "",
        invoice_payment_type: "",
        currency: "",
        currency_rate: 1,
        payment_date: moment().format("YYYY-MM-DD"),
        payment_date_account: moment().format("YYYY-MM-DD"),
        payment_by: "",
        amount: "",
        bank_account_id: null,
        note: "",
        consumed: "",
        entity_payment_type: "",
      },
      datesAccounting: {
        start_date: "",
        end_date: "",
      },

    };
  },
  computed: {
    paymentDateFormat() {
      return moment(this.paymentData.payment_date).format("DD/MM/YYYY");
    },

    sectionTitle() {
      if (!this.useEntity) return "Contrapartida Contable";
      return this.type === 1
        ? "Información del Proveedor"
        : "Información del Cliente";
    },

    sectionIcon() {
      if (!this.useEntity) return "mdi-book-open-page-variant";
      return this.type === 1 ? "mdi-account-tie" : "mdi-truck";
    },

    baseCurrencyCode() {
      const base = this.currencies.find(
        (c) => parseFloat(c.value) === 1 || c.is_base === true
      );
      return base ? base.description : "Moneda base";
    },

    baseCurrencyIso() {
      const base = this.currencies.find(
        (c) => parseFloat(c.value) === 1 || c.is_base === true
      );
      return base ? base.code : "DOP";
    },

    showExchangeRate() {
      const c = this.paymentData.currency;
      if (!c || !c.value) return false;
      return parseFloat(c.value) !== 1;
    },

    canShowSummary() {
      const hasCounter = this.useEntity
        ? !!this.entity.entity_id
        : !!(this.counterAccount && this.counterAccount.id);
      return !!(
        this.paymentData.bank_account_id &&
        this.paymentData.bank_account_id.account_id &&
        this.paymentData.amount > 0 &&
        hasCounter
      );
    },

    summary() {
      const rate = parseFloat(this.paymentData.currency_rate) || 1;
      const amount = parseFloat(this.paymentData.amount) || 0;
      const baseAmount = amount / rate;

      const bankLabel = this.paymentData.bank_account_id
        ? this.paymentData.bank_account_id.text
        : "Banco";
      const counterLabel = this.useEntity
        ? this.entity.name || "—"
        : this.counterAccount
        ? this.counterAccount.name
        : "—";

      // type 1 (Pago): Contrapartida DEBE / Banco HABER
      // type 2 (Cobro): Banco DEBE / Contrapartida HABER
      const debit =
        this.type === 1
          ? { label: counterLabel }
          : { label: bankLabel };
      const credit =
        this.type === 1
          ? { label: bankLabel }
          : { label: counterLabel };

      return {
        debit,
        credit,
        baseAmount,
        showForeign: rate !== 1,
      };
    },

    isFormValid() {
      const entityOrCounter = this.useEntity
        ? !!this.entity.entity_id
        : !!(this.counterAccount && this.counterAccount.id);
      return (
        this.paymentData.business_unit_id &&
        this.paymentData.location_id &&
        entityOrCounter &&
        this.paymentData.invoice_payment_type &&
        this.paymentData.amount > 0 &&
        this.paymentData.bank_account_id
      );
    },
  },
  watch: {
    dialog(newVal) {
      this.dialogInternal = newVal;
      if (newVal && this.entityPaymentTypes.length > 0) {
        this.paymentData.entity_payment_type = this.entityPaymentTypes[0];
      }
    },
    dialogInternal(newVal) {
      if (!newVal) {
        this.$emit("close");
      }
    },
  },
  mounted() {
    if (this.filters.business!=undefined && this.filters.business!=0) {
      this.paymentData.business_unit_id=this.filters.business
    }
    if (this.filters.location!=undefined && this.filters.location!=0) {
      this.paymentData.location_id=this.filters.location
      this.updateBanks()
    }
  },
  methods: {
    closeDialog() {
      this.dialogInternal = false;
      this.resetForm();
      this.$emit("close");
    },
    resetForm() {
      this.useEntity = true;
      this.counterAccount = null;
      this.entity = {
        entity_id: "",
        document_id: "",
        name: "",
        email: "",
        phone_number: "",
      };
      this.paymentData = {
        business_unit_id: "",
        location_id: "",
        department: "",
        invoice_payment_type: "",
        currency: "",
        currency_rate: 1,
        payment_date: moment().format("YYYY-MM-DD"),
        payment_date_account: moment().format("YYYY-MM-DD"),
        payment_by: "",
        amount: "",
        bank_account_id: null,
        note: "",
        consumed: "",
        entity_payment_type: "",
      };
      this.accountBank = [];
    },
    selectItem(item) {
      this.entity.entity_id = item.entity_id;
      this.entity.document_id = item.document_id;
      this.entity.name = item.name;
      this.entity.email = item.email;
      this.entity.phone_number = item.phone_number;
    },
    onUseEntityChange(val) {
      if (val) {
        this.counterAccount = null;
      } else {
        this.entity = {
          entity_id: "",
          document_id: "",
          name: "",
          email: "",
          phone_number: "",
        };
      }
    },
    async updateBanks() {
      this.accountBank = [];
      let business = await this.g_bussines();

      const resultCurrencies = await this.api_Post("accounting/currenciesBusiness", {
        business: this.paymentData.business_unit_id,
        effective_date: this.paymentData.payment_date_account,
      });
      const currencies = await resultCurrencies.json();
      this.$emit("update-currencies", currencies);

      let datas = await business.filter(
        (element) => element.id == this.paymentData.business_unit_id
      );
      for (let item of datas[0].business_unit_accounts) {
        this.accountBank.push({
          account_id: item.account_id,
          account_name: item.account_name,
          account_type_id: item.account_type_id,
          account_type_description: item.account_type_description,
          account_bank_id: item.account_bank_id,
          bank_description_long: item.bank_description_long,
          account_check_digit: item.account_check_digit,
          currency_id: item.currency_id,
          currency_description: item.currency_description,
          text: item.bank_description_long + " ( Cta. " + item.account_check_digit + " )",
          chart_of_account_id: item.chart_of_account_id,
        });
      }

      this.datesAccounting.start_date = "";
      this.datesAccounting.end_date = "";
      let request_calendar = {
        business_unit: this.paymentData.business_unit_id,
        module: 2,
      };
      const request = await this.api_Post(
        "accounting/get_calendar_accounting",
        request_calendar
      );
      if (request.status == 200) {
        const data = await request.json();
        if (data.length > 0) {
          this.datesAccounting.start_date = moment(data[0].from_date).format(
            "YYYY-MM-DD"
          );
          this.datesAccounting.end_date = moment(data[0].to_date).format("YYYY-MM-DD");
          this.paymentData.payment_date_account = moment(data[0].from_date).format(
            "YYYY-MM-DD"
          );
        }
      }
    },

    getIconBank(bankId) {
      return "";
    },
    currencyFormat(amount, isoCode = "DOP") {
      try {
        return new Intl.NumberFormat("es-DO", {
          style: "currency",
          currency: isoCode,
        }).format(amount || 0);
      } catch {
        return new Intl.NumberFormat("es-DO", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(amount || 0);
      }
    },
    async save() {
      this.loading = true;

      // Validaciones mejoradas
      if (!this.paymentData.business_unit_id) {
        this.$toast.warning("Elige una unidad de negocio");
        this.loading = false;
        return;
      }
      if (!this.paymentData.location_id) {
        this.$toast.warning("Elige una ubicación");
        this.loading = false;
        return;
      }
      if (this.useEntity && !this.entity.entity_id) {
        this.$toast.warning(`Elige un ${this.type === 1 ? "proveedor" : "cliente"}`);
        this.loading = false;
        return;
      }
      if (!this.useEntity && (!this.counterAccount || !this.counterAccount.id)) {
        this.$toast.warning("Elige una cuenta contable de contrapartida");
        this.loading = false;
        return;
      }
      if (
        !this.paymentData.invoice_payment_type ||
        !this.paymentData.invoice_payment_type.id
      ) {
        this.$toast.warning("Elige un método de pago");
        this.loading = false;
        return;
      }
      if (!this.paymentData.amount || this.paymentData.amount <= 0) {
        this.$toast.warning("Ingresa un monto válido");
        this.loading = false;
        return;
      }
      if (
        !this.paymentData.bank_account_id ||
        !this.paymentData.bank_account_id.account_id
      ) {
        this.$toast.warning("Elige una cuenta de banco");
        this.loading = false;
        return;
      }

      // Único endpoint, único SQL. La función decide la contrapartida:
      //   - useEntity = true  → cuenta del tipo de la entidad
      //   - useEntity = false → cuenta contable que envía el frontend
      const dataPayment = {
        in_direct_payment_id: null,
        in_invoice_type_id: this.type,
        in_invoice_payment_type_id: this.paymentData.invoice_payment_type.id,
        in_entity_id: this.useEntity ? this.entity.entity_id : null,
        in_business_unit_id: this.paymentData.business_unit_id,
        in_location_id: this.paymentData.location_id,
        in_payment_by: this.$store.state.auth.user.user.user_id,
        in_currency_id: this.paymentData.currency.id,
        in_payment_date: this.paymentData.payment_date,
        in_account_date: this.paymentData.payment_date_account,
        in_bank_account_id: this.paymentData.bank_account_id.account_id,
        in_total_amount: this.paymentData.amount,
        in_note: this.paymentData.note,
        in_counter_chart_of_account_id:
          !this.useEntity && this.counterAccount ? this.counterAccount.id : null,
      };

      try {
        const responseRequest = await this.api_Post(
          "transactions/save_direct_payment_entity",
          dataPayment
        );

        if (responseRequest.status == 200) {
          let data = await responseRequest.json();
          this.$emit("saved", data);
          this.closeDialog();
        } else {
          let data = await responseRequest.json();
          this.$toast.error(data.error || "Error al guardar");
        }
      } catch (error) {
        this.$toast.error("Error de conexión: " + error.message);
      } finally {
        this.loading = false;
      }
    },
    set_currency() {
      this.paymentData.currency = this.currencies.find(
        (element) => element.id == this.paymentData.bank_account_id.currency_id
      );
      this.onCurrencyChange();
    },
    onCurrencyChange() {
      if (this.paymentData.currency && this.paymentData.currency.value) {
        this.paymentData.currency_rate = parseFloat(
          this.paymentData.currency.value
        );
      } else {
        this.paymentData.currency_rate = 1;
      }
    },
  },
};
</script>

<style scoped>
.payment-dialog {
  border-radius: 12px !important;
  overflow: hidden;
}

.payment-header {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
  border-radius: 0 !important;
}

.section-card {
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

.section-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.section-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  padding: 12px 16px;
  margin: 0;
  font-size: 16px;
  color: #495057;
}

.accounts-table {
  border-radius: 8px;
  overflow: hidden;
}

.accounts-table ::v-deep .v-data-table__wrapper {
  border-radius: 8px;
}

.expanded-content {
  background-color: #f8f9fa;
  border-top: 1px solid #e9ecef;
}

.total-card {
  border-radius: 12px !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.total-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 25px rgba(0, 0, 0, 0.15);
}

::v-deep .v-text-field--outlined .v-input__control .v-input__slot {
  border-radius: 8px;
}

::v-deep .v-autocomplete--outlined .v-input__control .v-input__slot {
  border-radius: 8px;
}

::v-deep .v-btn {
  border-radius: 8px;
  text-transform: none;
  font-weight: 500;
}

::v-deep .v-card {
  border-radius: 8px;
}

::v-deep
  .v-data-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr:hover:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper) {
  background: rgba(25, 118, 210, 0.04);
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .payment-dialog {
    margin: 8px;
  }

  .section-card {
    margin-bottom: 16px;
  }

  .v-card-text {
    padding: 16px;
  }
}

@media (max-width: 600px) {
  .payment-header .text-h5 {
    font-size: 1.2rem !important;
  }

  .section-header {
    font-size: 14px;
  }

  .total-card .text-h4 {
    font-size: 1.5rem !important;
  }
}

/* Loading state styles */
.v-btn--loading {
  pointer-events: none;
}

/* Form validation styles */
::v-deep .v-text-field--error .v-input__control .v-input__slot {
  border-color: #f44336 !important;
}

::v-deep .v-text-field--success .v-input__control .v-input__slot {
  border-color: #4caf50 !important;
}

/* Animation for new rows */
.v-data-table tbody tr {
  transition: all 0.3s ease;
}

/* Chip animations */
.v-chip {
  transition: all 0.3s ease;
}

/* Focus styles */
::v-deep .v-input--is-focused .v-input__control .v-input__slot {
  border-color: #1976d2 !important;
  border-width: 2px !important;
}
</style>
